import { Button, Flex, Form, Input, InputNumber } from "antd";
import { useEffect } from "react";
import { useParams } from "react-router";
import {
  useCreateQuizMutation,
  useUpdateQuizMutation,
} from "../../../../app/features/adminContent/adminContent.api";
import CustomModal from "../../../../components/CustomModal";
import useError from "../../../../hooks/useError";
import type { ModalType } from "../../../../hooks/useModal";
import { toast } from "../../../../lib/toast";

interface Props {
  open: boolean;
  onCancel: () => void;
  initialValues?: any | null;
  type?: ModalType["type"];
}

export default function ManageTopicsModal({
  open,
  onCancel,
  initialValues,
  type,
}: Props) {
  const { courseId, sectionId } = useParams();
  const [form] = Form.useForm();

  // Mutation API
  const [
    createQuiz,
    { isLoading: createLoading, isSuccess: createSuccess, error: createError },
  ] = useCreateQuizMutation();
  const [
    updateQuiz,
    { isLoading: updateLoading, isSuccess: updateSuccess, error: updateError },
  ] = useUpdateQuizMutation();
  // Error Handeling
  useError(createError, "Quiz Topics Create Failed");
  useError(updateError, "Quiz Topics Update Failed");

  const handleSubmit = async (values: any) => {
    const newData = {
      ...values,

      gradingMode: "LAST_SCORE",
      timeLimitSec: Number(values.timeLimitSec) * 60,

      // TODO:
      passMarkPercent: 50,
      sortOrder: 1,
    };

    if (type !== "UPDATE") {
      newData.courseId = Number(courseId);
      newData.sectionId = Number(sectionId);
    }

    if (initialValues && type === "UPDATE") {
      // 🔥 call mutation with both id and body
      await updateQuiz({
        id: initialValues.id, // <--- pass the quiz id here
        body: newData, // <--- pass the updated data
      });
    } else {
      await createQuiz(newData);
    }
  };

  useEffect(() => {
    if (initialValues && type === "UPDATE") {
      form.setFieldsValue({
        title: initialValues?.title,
        timeLimitSec: initialValues?.timeLimitSec
          ? initialValues.timeLimitSec / 60 // ✅ convert to minutes
          : 0,
        passMarkPercent: initialValues?.passMarkPercent,
        sortOrder: initialValues?.sortOrder,
      });
    }
  }, [initialValues, form, open, type]);

  useEffect(() => {
    if (createSuccess) {
      toast("success", "Quiz Topics Created Successfully");
      onCancel();
    }
  }, [createSuccess]);

  useEffect(() => {
    if (updateSuccess) {
      toast("success", "Quiz Topics Updated Successfully");
      onCancel();
    }
  }, [updateSuccess]);

  return (
    <CustomModal
      handleClose={onCancel}
      title={type === "UPDATE" ? "Update Topics" : "Create Topics"}
      open={open}
      width={500}
      footer
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="custom-form"
        size="large"
      >
        <Form.Item
          label="Topics Title"
          name="title"
          rules={[{ required: true, message: "Please enter topics title" }]}
        >
          <Input placeholder="Enter Topics Title" />
        </Form.Item>

        <Form.Item
          label="Time Limit (Minute)"
          name="timeLimitSec"
          rules={[
            { required: true, message: "Please enter time limit (Minute)" },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            placeholder="Enter Time Limit (Minute)"
          />
        </Form.Item>

        {/* <Form.Item
          label="Pass Mark (%)"
          name="passMarkPercent"
          rules={[{ required: true, message: "Please enter pass mark (%)" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            placeholder="Enter Pass Mark (%)"
          />
        </Form.Item>

        <Form.Item
          label="Sort Order"
          name="sortOrder"
          rules={[{ required: true, message: "Please enter sort order" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            placeholder="Enter sort order"
          />
        </Form.Item> */}

        <Flex justify="end" gap={8} className="mt-6">
          <Button
            className="min-w-[120px]"
            disabled={createLoading || updateLoading}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="min-w-[120px]"
            htmlType="submit"
            loading={createLoading || updateLoading}
          >
            {type === "UPDATE" ? "Update" : "Create"}
          </Button>
        </Flex>
      </Form>
    </CustomModal>
  );
}
